import React, { useState, useEffect } from 'react';
import PageMeta from '../components/PageMeta';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, CheckCircle2, Star, Users, Award, Globe, TrendingUp, Target,
  Zap, BookOpen, Clock, Calendar, Phone, Mail, ChevronDown, Sparkles,
  Shield, Rocket, GraduationCap, Brain, Lightbulb, Eye, Loader2,
  Film, Camera, Clapperboard, MonitorPlay, Palette, Mic2, Video
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { trackWhatsAppClick, trackFormSubmission } from '../utils/analytics';
import CertificatesSection from '../components/CertificatesSection';
import AdmissionEnrollment from '../components/AdmissionEnrollment';
import EnquireNowButton from '../components/EnquireNowButton';
import HeroPricingCard from '../components/HeroPricingCard';
import BrandLogo from '../components/BrandLogo';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import MobileCTABar from '../components/MobileCTABar';

const FilmMakingCoursePage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/leads`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: formData.name, email: formData.email, phone: formData.phone, message: formData.message, course: 'Film Making & Cinematic Animation (4 Months)', source: 'film-making-page' })
      });
      if (res.ok) { trackFormSubmission('film_making_application', { course: 'Film Making' }); navigate('/thank-you'); }
    } catch (err) { console.error(err); }
    setSubmitting(false);
  };

  const [payForm, setPayForm] = useState({ payName: '', payEmail: '', payPhone: '' });
  const [paySubmitting, setPaySubmitting] = useState(false);

  const handleAdmissionPayment = async (e) => {
    e.preventDefault();
    setPaySubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/payments/initiate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firstname: payForm.payName, email: payForm.payEmail, phone: payForm.payPhone, course_name: 'Film Making & Cinematic Animation - Admission Fee', amount: 3000 })
      });
      if (res.ok) { const html = await res.text(); document.open(); document.write(html); document.close(); }
    } catch (err) { console.error(err); }
    setPaySubmitting(false);
  };

  const scrollToForm = () => document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' });

  const curriculum = [
    { month: 'Month 1', title: 'Film Making & Editing Foundations', topics: ['Basics of Film Making & Visual Language', 'Camera Understanding & Angles', 'Editing Fundamentals & Workflow', 'Adobe Premiere Pro Deep Dive'] },
    { month: 'Month 2', title: 'Cinematic Editing & Sound', topics: ['Sound Syncing & Audio Mixing', 'Transitions, Cuts & Pacing', 'Color Grading & Visual Enhancements', 'Shot Composition & Framing'] },
    { month: 'Month 3', title: 'VFX, Motion Graphics & Blender', topics: ['Green Screen & Chroma Keying', 'After Effects: Motion Graphics & Titles', 'Blender for Cinematics & 3D Scenes', 'Cinematic Lighting & Camera Animation'] },
    { month: 'Month 4', title: 'AI Film Making & Portfolio', topics: ['AI Video Generation & Voice Tools', 'Automated Editing Workflows', 'Complete Short Film Project', 'Professional Showreel & Portfolio'] }
  ];

  const careers = ['Film Editor', 'VFX Artist', 'Animation Director', 'Cinematic Designer', 'Content Creator', 'YouTube Studio Artist', 'OTT Production Team', 'Motion Graphics Designer'];
  const tools = ['Adobe Premiere Pro', 'After Effects', 'Blender', 'DaVinci Resolve', 'Runway ML', 'Midjourney', 'ElevenLabs', 'Unreal Engine'];

  const faqs = [
    { q: 'Do I need prior film making experience?', a: 'No! This course starts from absolute basics and gradually builds up to professional-level production skills.' },
    { q: 'Will I get to work on real projects?', a: 'Yes. You will produce a complete short film and build a professional showreel by the end of the program.' },
    { q: 'Is this course available online?', a: 'We offer a hybrid model — offline + online. Live classes with recordings available for revision.' },
    { q: 'What career opportunities can I expect?', a: 'Graduates work as film editors, VFX artists, content creators, and in OTT production teams. Freelancing opportunities are also excellent.' },
    { q: 'What is the refund policy?', a: 'We offer a 7-day money-back guarantee from the program start date.' }
  ];

  const testimonials = [
    { name: 'Arjun Mehta', role: 'Now working as VFX Artist', quote: 'This course transformed my passion for films into a real career. The hands-on projects were incredible.' },
    { name: 'Priya Singh', role: 'YouTube Creator, 50K+ Subs', quote: 'The AI film making module was a game-changer. I now produce content 3x faster with professional quality.' },
    { name: 'Rahul Verma', role: 'Freelance Editor', quote: 'Within 2 months of completing, I landed my first paying client. The portfolio we built was the key.' }
  ];

  return (
    <div className="min-h-screen bg-white text-slate-900 overflow-x-hidden relative">
      <PageMeta
        title="Film Making & Animation Course | Job-Assured | AI School for All"
        description="Professional film making and animation. Industry tools, real projects, job assurance. Enroll at AI School for All."
        path="/film-making-animation-course"
      />
      <div className="relative z-10">
      {/* STICKY MARQUEE */}
      <div className="bg-gradient-to-r from-orange-600 via-amber-600 to-orange-700 py-3">
        <div className="flex items-center justify-center gap-4 px-4">
          <div className="hidden md:flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-yellow-200 animate-pulse" />
            <span className="text-sm font-bold text-white">Admissions Open for Admissions Open</span>
            <span className="text-slate-900/60">|</span>
            <span className="text-sm text-yellow-200 font-semibold">Limited Seats — 63% OFF!</span>
          </div>
          <Button onClick={scrollToForm} className="bg-white text-orange-700 hover:bg-yellow-200 px-6 py-2 text-sm font-bold rounded-full">
            Reserve Your Seat <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
      <SiteHeader />


      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-12 md:pt-16 overflow-hidden text-white">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1777024667978-0867f07b4485?w=1920&h=1080&fit=crop&q=80" alt="Cinematic Camera" className="w-full h-full object-cover" decoding="async" fetchPriority="high" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/80 to-orange-900/40"></div>
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'linear-gradient(rgba(251,146,60,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(251,146,60,0.3) 1px, transparent 1px)', backgroundSize: '60px 60px' }}></div>
        </div>
        <div className="container mx-auto max-w-7xl px-6 relative z-10">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/30 rounded-full px-4 py-2 mb-6">
              <Film className="w-4 h-4 text-orange-400" />
              <span className="text-sm font-bold text-orange-300">4-Month Industry Program</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-black mb-6 leading-tight">
              Master Film Making, VFX &<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-300">Cinematic Animation</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed max-w-2xl">Learn cinematic storytelling, animation production, editing, VFX, AI-powered filmmaking and portfolio creation with real-world projects.</p>
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button onClick={scrollToForm} className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-8 py-6 text-lg font-bold rounded-xl">
                Enroll Now (50% OFF) <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <EnquireNowButton courseName="Film Making & Cinematic Animation" size="md" label="Enquire Now" variant="outline" />
            </div>
            <div className="flex flex-wrap gap-4">
              {['Offline + Online Hybrid', 'Industry Ready Program', 'Limited Seats'].map(t => (
                <div key={t} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2">
                  <CheckCircle2 className="w-4 h-4 text-orange-400" /><span className="text-sm text-gray-300">{t}</span>
                </div>
              ))}
            </div>
            </div>
            <div className="lg:col-span-5">
              <HeroPricingCard
                courseName="Film Making & Cinematic Animation"
                oldPrice={95000}
                newPrice={35000}
                discountLabel="63% OFF"
                badge="4-Month Industry Program"
                batchDate="New Batches Every Month"
                theme="orange"
                whatsappMessage="Hi, I want to know about the Film Making Course"
              />
            </div>
          </div>
        </div>
      </section>

      {/* PREMIUM TRAINER PROFILE - 3rd Section */}
      <section className="py-24 px-6 bg-gradient-to-br from-black via-orange-950/40 to-amber-950/30 relative overflow-hidden">
        <div className="absolute top-1/4 left-0 w-[500px] h-[500px] rounded-full bg-orange-500/15 blur-[140px]"></div>
        <div className="absolute bottom-1/4 right-0 w-[400px] h-[400px] rounded-full bg-amber-500/15 blur-[120px]"></div>
        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'repeating-linear-gradient(45deg, rgba(251,146,60,0.3) 0 2px, transparent 2px 60px)' }}></div>

        <div className="container mx-auto max-w-7xl relative">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/40 rounded-full px-5 py-2 mb-5">
              <Award className="w-4 h-4 text-orange-400" />
              <span className="text-xs font-bold text-orange-300 uppercase tracking-widest">Meet Your Industry Mentor</span>
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-4 leading-tight">
              Learn From a <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-amber-300 to-yellow-300">Real Film Maker</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">Hands-on training by a working industry professional — not just theory.</p>
          </div>

          <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-center">
            {/* TRAINER PHOTO - large */}
            <div className="lg:col-span-2 relative">
              <div className="relative group">
                <div className="absolute -inset-2 bg-gradient-to-r from-orange-500 via-amber-400 to-yellow-500 rounded-3xl blur-xl opacity-40 group-hover:opacity-70 transition-opacity"></div>
                <div className="relative rounded-3xl overflow-hidden ring-4 ring-orange-500/40 shadow-2xl shadow-orange-500/30">
                  <img
                    src="https://customer-assets.emergentagent.com/job_8d33ea52-c764-4e32-b8e2-de0e8040a6fa/artifacts/jyd37jw6_WhatsApp%20Image%202026-05-09%20at%2010.54.31.jpeg"
                    alt="Mr. Our Expert Trainers — Lead Trainer"
                    className="w-full aspect-[4/5] object-cover"
                    data-testid="trainer-saurabh-photo" decoding="async" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <p className="text-xs font-bold text-orange-300 uppercase tracking-widest mb-1">Lead Trainer</p>
                    <p className="text-3xl font-black text-white">Mr. Our Expert Trainers</p>
                  </div>
                </div>
                {/* Floating credential badges */}
                <div className="absolute -top-4 -right-4 bg-gradient-to-br from-orange-500 to-amber-600 text-white rounded-2xl px-4 py-3 shadow-2xl rotate-3 hidden md:block">
                  <p className="text-xs font-bold opacity-90">EXPERIENCE</p>
                  <p className="text-2xl font-black">15+ Yrs</p>
                </div>
                <div className="absolute -bottom-4 -left-4 bg-white text-black rounded-2xl px-4 py-3 shadow-2xl -rotate-3 hidden md:block">
                  <p className="text-xs font-bold opacity-70">STUDENTS</p>
                  <p className="text-2xl font-black">5,000+</p>
                </div>
              </div>
            </div>

            {/* TRAINER INFO - right side */}
            <div className="lg:col-span-3 space-y-6">
              <div>
                <h3 className="text-5xl md:text-6xl font-black text-white mb-3 leading-none">Mr. Our Expert Trainers</h3>
                <p className="text-xl text-orange-300 font-semibold">Lead Trainer — Film Making, VFX &amp; Cinematic Animation</p>
              </div>

              {/* Stats grid */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { num: '15+', label: 'Years Experience' },
                  { num: '5,000+', label: 'Students Trained' },
                  { num: '200+', label: 'Industry Projects' }
                ].map((s, i) => (
                  <div key={i} className="bg-gradient-to-br from-orange-500/10 to-amber-500/5 border border-orange-500/30 rounded-2xl p-4 text-center backdrop-blur-sm">
                    <p className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-br from-orange-300 to-amber-400">{s.num}</p>
                    <p className="text-xs text-gray-400 mt-1">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Bio */}
              <p className="text-base md:text-lg text-gray-300 leading-relaxed">
                A passionate film maker and VFX professional with over <span className="font-bold text-orange-300">15 years</span> in the creative industry. Expert Trainer has trained thousands of aspiring artists across India, mentoring students from absolute beginners to industry-ready professionals working at OTT studios, ad agencies, and gaming companies.
              </p>

              {/* Expertise checklist */}
              <div className="grid sm:grid-cols-2 gap-x-4 gap-y-3 pt-2">
                {[
                  'Cinematic storytelling &amp; editing mastery',
                  'Blender 3D, VFX &amp; post-production expert',
                  'AI-powered filmmaking workflows',
                  'Real production pipelines &amp; client work',
                  'Hands-on, project-based teaching',
                  'Personal mentorship until placement'
                ].map((t, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-gray-300">
                    <CheckCircle2 className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                    <span dangerouslySetInnerHTML={{ __html: t }} />
                  </div>
                ))}
              </div>

              {/* Quote */}
              <div className="border-l-4 border-orange-500 pl-5 py-2 italic text-gray-300 bg-orange-500/5 rounded-r-xl">
                "I don't just teach techniques — I help you build a real career. Every student gets a portfolio that opens doors."
                <p className="not-italic text-orange-400 font-bold text-sm mt-2">— Mr. Our Expert Trainers</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY FILM MAKING */}
      <section className="py-24 px-6 bg-white relative">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(251,146,60,0.15) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(245,158,11,0.15) 0%, transparent 50%)' }}></div>
        <div className="container mx-auto max-w-6xl relative">
          <h2 className="text-4xl sm:text-5xl font-black text-center mb-4">Why Film Making is <span className="text-orange-600">the Future</span></h2>
          <p className="text-slate-600 text-center mb-16 max-w-2xl mx-auto">The creative industry is booming — OTT, YouTube, AI cinema. Now is the best time to build your career.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <MonitorPlay className="w-8 h-8" />, title: 'OTT Revolution', desc: 'Netflix, Prime & Disney+ are spending billions on original content' },
              { icon: <Video className="w-8 h-8" />, title: 'Creator Economy', desc: 'YouTube & Instagram creators earn lakhs monthly with video content' },
              { icon: <Brain className="w-8 h-8" />, title: 'AI Cinema', desc: 'AI tools are revolutionizing film production and post-production' },
              { icon: <TrendingUp className="w-8 h-8" />, title: 'High Demand', desc: 'Editors, VFX artists & motion designers are in massive demand' }
            ].map((item, i) => (
              <div key={i} className="bg-slate-50 border border-slate-200 rounded-2xl p-6 hover:border-orange-400 transition-all">
                <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center text-orange-600 mb-4">{item.icon}</div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CAREER OPPORTUNITIES */}
      <section className="py-24 px-6 bg-white relative">
        <div className="absolute top-10 right-10 w-32 h-32 rounded-full bg-orange-500/20 blur-3xl"></div>
        <div className="container mx-auto max-w-6xl relative">
          <h2 className="text-4xl sm:text-5xl font-black text-center mb-16">Career <span className="text-orange-600">Opportunities</span></h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="rounded-2xl overflow-hidden border border-slate-200">
              <img src="https://images.unsplash.com/photo-1757845524683-611470b2d7ce?w=600&h=350&fit=crop&q=80" alt="Film Production" className="w-full h-48 object-cover" decoding="async" loading="lazy" />
              <div className="p-6 bg-slate-50">
                <h3 className="text-lg font-bold text-slate-900 mb-3">Production & Post-Production</h3>
                <div className="flex flex-wrap gap-2">
                  {['Film Editor', 'VFX Artist', 'Animation Director', 'Motion Graphics Designer'].map((r, i) => (
                    <span key={i} className="bg-orange-100 text-orange-600 text-xs font-semibold px-3 py-1.5 rounded-full border border-orange-200">{r}</span>
                  ))}
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden border border-slate-200">
              <img src="https://images.unsplash.com/photo-1579109652910-99b9be06aaec?w=600&h=350&fit=crop&q=80" alt="Content Creation" className="w-full h-48 object-cover" decoding="async" loading="lazy" />
              <div className="p-6 bg-slate-50">
                <h3 className="text-lg font-bold text-slate-900 mb-3">Content & Creative</h3>
                <div className="flex flex-wrap gap-2">
                  {['Cinematic Designer', 'Content Creator', 'YouTube Studio Artist', 'OTT Production Team'].map((r, i) => (
                    <span key={i} className="bg-orange-100 text-orange-600 text-xs font-semibold px-3 py-1.5 rounded-full border border-orange-200">{r}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="mt-12 text-center">
            <p className="text-slate-600">Average starting salary: <span className="text-orange-600 font-bold text-2xl">&#8377;4-12 LPA</span> | Freelancers earn <span className="text-orange-600 font-bold text-2xl">&#8377;30K-1L/month</span></p>
          </div>
        </div>
      </section>

      {/* CURRICULUM */}
      <section id="curriculum" className="py-24 px-6 bg-white relative">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'linear-gradient(45deg, rgba(251,146,60,0.2) 25%, transparent 25%, transparent 75%, rgba(251,146,60,0.2) 75%), linear-gradient(45deg, rgba(251,146,60,0.2) 25%, transparent 25%, transparent 75%, rgba(251,146,60,0.2) 75%)', backgroundSize: '60px 60px', backgroundPosition: '0 0, 30px 30px' }}></div>
        <div className="container mx-auto max-w-6xl relative">
          <h2 className="text-4xl sm:text-5xl font-black text-center mb-4">4-Month <span className="text-orange-600">Curriculum</span></h2>
          <p className="text-slate-600 text-center mb-16">Industry-aligned, project-based learning journey</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {curriculum.map((m, i) => (
              <div key={i} className="bg-slate-50 border border-slate-200 rounded-2xl p-6 hover:border-orange-400 transition-all group">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs font-bold uppercase tracking-wider text-orange-600 bg-orange-100 px-3 py-1 rounded-full">{m.month}</span>
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-4">{m.title}</h3>
                <ul className="space-y-2">{m.topics.map((t, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm text-slate-600"><CheckCircle2 className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />{t}</li>
                ))}</ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STUDENT WORK SHOWCASE — Horizontal Scrolling Gallery */}
      <section className="py-24 px-0 bg-gradient-to-br from-black via-red-950/30 to-orange-950/30 relative overflow-hidden">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[80%] h-[2px] bg-gradient-to-r from-transparent via-orange-500/40 to-transparent"></div>
        <div className="absolute top-1/2 right-0 w-[400px] h-[400px] bg-orange-500/10 rounded-full blur-[140px]"></div>
        <div className="absolute top-1/2 left-0 w-[400px] h-[400px] bg-amber-500/10 rounded-full blur-[140px]"></div>

        <div className="container mx-auto max-w-7xl px-6 relative mb-12">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/40 rounded-full px-4 py-1.5 mb-4">
                <Sparkles className="w-4 h-4 text-orange-400" />
                <span className="text-xs font-bold text-orange-300 uppercase tracking-widest">Real Work By Our Students</span>
              </div>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight">
                Student <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-amber-300 to-yellow-300">Showreel</span>
              </h2>
              <p className="text-lg text-gray-400 mt-3 max-w-xl">Films, VFX shots & cinematic projects produced by our students during the program.</p>
            </div>
            <div className="text-sm text-orange-300 font-semibold flex items-center gap-2">
              <span className="hidden md:inline">Scroll horizontally</span>
              <ArrowRight className="w-4 h-4 animate-pulse" />
            </div>
          </div>
        </div>

        <div data-testid="student-showcase-scroll" className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-8 px-6 md:px-12 scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
          {[
            { title: 'Short Film Production', tag: 'CINEMA', img: 'https://images.unsplash.com/photo-1485846234645-a62644f84728?w=700&h=500&fit=crop&q=80', desc: 'Complete short films shot with professional cameras, lighting & editing workflows.' },
            { title: 'VFX & Green Screen', tag: 'COMPOSITING', img: 'https://images.unsplash.com/photo-1574375927938-d5a98e8ffe85?w=700&h=500&fit=crop&q=80', desc: 'Chroma keying, layering & visual effects for a professional VFX showreel.' },
            { title: 'Cinematic Color Grading', tag: 'DaVinci Resolve', img: 'https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?w=700&h=500&fit=crop&q=80', desc: 'Professional color grading projects with cinematic look development.' },
            { title: 'Motion Graphics & Titles', tag: 'After Effects', img: 'https://images.unsplash.com/photo-1626785774573-4b799315345d?w=700&h=500&fit=crop&q=80', desc: 'Title sequences, lower-thirds & broadcast-quality motion graphics.' },
            { title: 'Blender Cinematics', tag: '3D ANIMATION', img: 'https://images.unsplash.com/photo-1633613286848-e6f43bbafb8d?w=700&h=500&fit=crop&q=80', desc: 'Cinematic 3D scenes & camera animation rendered in Blender.' },
            { title: 'AI-Enhanced Cinema', tag: 'AI WORKFLOW', img: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=700&h=500&fit=crop&q=80', desc: 'AI video generation, voice synthesis & automated editing pipelines.' },
            { title: 'Director\u2019s Showreel', tag: 'PORTFOLIO', img: 'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=700&h=500&fit=crop&q=80', desc: 'Industry-ready final showreel — what students walk away with.' }
          ].map((p, i) => (
            <div key={i} className="snap-start flex-shrink-0 w-[300px] md:w-[380px] group cursor-pointer">
              <div className="relative rounded-2xl overflow-hidden border border-orange-500/20 hover:border-orange-400/60 transition-all">
                <img src={p.img} alt={p.title} className="w-full h-[260px] md:h-[300px] object-cover group-hover:scale-110 transition-transform duration-700" decoding="async" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent"></div>
                <div className="absolute top-3 left-3">
                  <span className="bg-orange-500/90 text-white text-[10px] font-black px-2.5 py-1 rounded-md tracking-widest backdrop-blur-sm">{p.tag}</span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="text-xl font-black text-white mb-2">{p.title}</h3>
                  <p className="text-sm text-gray-300 line-clamp-2">{p.desc}</p>
                </div>
              </div>
            </div>
          ))}
          {/* End spacer */}
          <div className="flex-shrink-0 w-6"></div>
        </div>

        <div className="text-center mt-6 text-sm text-gray-500">
          <span className="inline-flex items-center gap-2"><Sparkles className="w-3 h-3 text-orange-400" /> Swipe / drag to explore <Sparkles className="w-3 h-3 text-orange-400" /></span>
        </div>
      </section>

      <CertificatesSection theme="orange" courseName="Mastering Film Making & Editing" />

      {/* WHY AI SCHOOL FOR ALL */}
      <section className="py-24 px-6 bg-white relative">
        <div className="container mx-auto max-w-6xl relative">
          <h2 className="text-4xl sm:text-5xl font-black text-center mb-16">Why <span className="text-orange-600">AI School For All</span></h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: <Award />, title: "Workshop Library's 20+ Year Legacy", desc: 'Backed by two decades of training excellence and industry trust.' },
              { icon: <Globe />, title: 'Global Training Ecosystem', desc: 'Students from across India and internationally trained successfully.' },
              { icon: <Target />, title: 'Industry-Aligned Curriculum', desc: 'Curriculum designed with active industry professionals and studios.' },
              { icon: <Rocket />, title: 'Real Project Exposure', desc: 'Work on actual client briefs and production pipelines.' },
              { icon: <Users />, title: 'Mentorship Support', desc: 'Personal mentorship from experienced industry professionals.' },
              { icon: <Palette />, title: 'Portfolio Building', desc: 'Graduate with a professional showreel ready for job applications.' }
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 bg-slate-50 border border-slate-200 rounded-xl p-5 hover:border-orange-400 transition-all">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center text-orange-600 flex-shrink-0">{item.icon}</div>
                <div><h3 className="font-bold text-slate-900 mb-1">{item.title}</h3><p className="text-sm text-slate-600">{item.desc}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 px-6 bg-white relative">
        <div className="container mx-auto max-w-6xl relative">
          <h2 className="text-4xl sm:text-5xl font-black text-center mb-16">What Students <span className="text-orange-600">Say</span></h2>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
                <div className="flex gap-1 mb-4">{[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-orange-400 text-orange-600" />)}</div>
                <p className="text-slate-700 mb-4 italic">"{t.quote}"</p>
                <div><p className="font-bold text-slate-900">{t.name}</p><p className="text-sm text-orange-600">{t.role}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CERTIFICATION + TOOLS */}
      <section className="py-24 px-6 bg-white relative">
        <div className="container mx-auto max-w-6xl relative">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-black mb-6">Industry <span className="text-orange-600">Certification</span></h2>
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-2xl p-8">
                <Award className="w-12 h-12 text-orange-600 mb-4" />
                <h3 className="text-xl font-bold text-slate-900 mb-2">AI School For All Certificate</h3>
                <p className="text-slate-600 mb-4">Powered by Workshop Library — recognized by industry partners and recruiters.</p>
                <ul className="space-y-2">
                  {['Industry-recognized credential', 'Portfolio + Showreel included', 'Placement assistance provided', 'Internship opportunities'].map((t, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle2 className="w-4 h-4 text-orange-600" />{t}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div>
              <h2 className="text-3xl font-black mb-6">Tools You'll <span className="text-orange-600">Master</span></h2>
              <div className="grid grid-cols-2 gap-3">
                {tools.map((t, i) => (
                  <div key={i} className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center hover:border-orange-400 transition-all">
                    <p className="text-sm font-semibold text-slate-900">{t}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 bg-white relative">
        <div className="container mx-auto max-w-3xl relative">
          <h2 className="text-4xl sm:text-5xl font-black text-center mb-16">Frequently Asked <span className="text-orange-600">Questions</span></h2>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left">
                  <span className="font-semibold text-slate-900 pr-4">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-orange-600 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && <div className="px-5 pb-5 text-slate-600 text-sm">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* DETAILED ENROLLMENT + ADMISSION PAYMENT */}
      <AdmissionEnrollment courseName="Film Making & Cinematic Animation" theme="orange" source="orange-course-page" />
      <MobileCTABar courseName="Film Making" />

<SiteFooter accent="orange" />

      {/* WHATSAPP FLOAT */}
      <a href="https://wa.me/919891550666?text=Hi, I want to know about the Film Making Course" target="_blank" rel="noopener noreferrer"
        onClick={() => trackWhatsAppClick('film_making_page')}
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-2xl transition-all hover:scale-110" data-testid="whatsapp-float">
        <Phone className="w-6 h-6" />
      </a>

      {/* STICKY MOBILE CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white/95 backdrop-blur-md border-t border-orange-200 shadow-2xl p-3">
        <Button onClick={scrollToForm} className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white py-3 font-bold rounded-xl">
          Enroll Now — &#8377;35,000 (63% OFF)
        </Button>
      </div>
      </div>
    </div>
  );
};

export default FilmMakingCoursePage;
